// TerrainMap + Path — passability/occupancy state and THE pathfinder. game/terrainmap.cpp
// + game/ht.h:1470-1607. Nearly pure sim (tile RENDERING lives in tilemap.cpp, not here).
//
// Determinism contracts (tasks/sim-core-map.md hotspot #8):
// - the open list is sorted ASCENDING by u16 wScore with '<=' insertion: new nodes go
//   BEFORE equal scores (LIFO ties) — never a heap;
// - wScoreKnown accumulates blocked/avoid costs of 10000/9000 in a WORD: u16 wraparound is
//   REACHABLE (~6+ blocked tiles) and reorders the list — reproduced via & 0xffff;
// - fixed neighbor order n=0..7 over g_mpDirToDx/Dy; step costs {5 straight, 7 diagonal};
// - 576-node lifetime cap (kcNodesMax = 64*3*3) -> best-so-far-toward-closest;
// - the Bresenham walkers tie-break 'dx > dy' STRICT (diagonals run y-major);
// - occupancy buffer: persistent kbfMobileUnit|kbfStructure|kbfReserved (0xE0, the save
//   mask) + transient pathfinder scratch kbfLinked|kbfHead|kbfDirMask; FindPath's aligned
//   fast-clear is a plain per-byte &= 0xE0 here (value-identical);
// - the Path follow-cache trio (m_idirCache/m_txCache/m_tyCache) is PERSISTED sim state.

import { s16 } from '../math/int32.ts';
import {
  g_mpDirToDx,
  g_mpDirToDy,
  kdirNW,
  kdirSW,
  kdirW,
  kdirNE,
  kdirSE,
  kdirE,
  kdirN,
  kdirS,
  kdirInvalid,
  kdir16Invalid,
} from '../math/dir.ts';
import type { TPoint } from '../types.ts';

// game/ht.h:1470-1475
export const kbfMobileUnit = 0x80;
export const kbfStructure = 0x40;
export const kbfReserved = 0x20;
export const kbfLinked = 0x10;
export const kbfHead = 0x08;
export const kbfDirMask = 0x07;

// game/ht.h:1550-1553
export const kttArea = 0;
export const kttOpen = 1;
export const kttWall = 2;
export const kttBlocked = 3;

// game/ht.h:758
export const ktcMax = 64;

// game/terrainmap.cpp:14-23
export const ganSquared: number[] = (() => {
  const a: number[] = [];
  for (let i = 0; i < 64; i++) a.push(i * i);
  return a;
})();

// game/terrainmap.cpp:25
export const gmpdirdirOpposite = [4, 5, 6, 7, 0, 1, 2, 3];

// game/terrainmap.cpp:32-39
const knScoreBlocked = 10000;
const knScoreStructure = 9000;
const s_anScoreTerrain = [0, 0, knScoreBlocked, knScoreBlocked];
const s_anScoreVector = [5, 7, 5, 7, 5, 7, 5, 7];

// game/terrainmap.cpp:44 — node futility cutoff
const kcNodesMax = ktcMax * 3 * 3;

const kbClear = kbfMobileUnit | kbfStructure | kbfReserved; // 0xE0

interface PathHead {
  // game/ht.h:1457-1467
  ppathhNext: PathHead | null;
  txLast: number;
  tyLast: number;
  cSteps: number;
  offHead: number; // word
  wScoreKnown: number; // word
  wScore: number; // word
}

export class TerrainMap {
  private m_terrain!: Uint8Array; // the .trm body (BIG-endian header parsed at the boundary)
  private m_abBuffer!: Uint8Array;
  private m_cbBuffer = 0;
  private m_ctx = 0;
  private m_cty = 0;
  private m_mpDirDelta = new Array<number>(8).fill(0);
  private m_ppathhList: PathHead | null = null;
  private m_ppathhFreeList: PathHead | null = null;
  private m_cNodes = 0;

  // game/terrainmap.cpp:69-94 — the pak-mapped .trm becomes a JSON/bytes input:
  // { ctx, cty, terrain } with terrain[ty*ctx+tx] in ktt* values.
  Init(ctx: number, cty: number, terrain: Uint8Array): boolean {
    this.m_ctx = ctx;
    this.m_cty = cty;
    this.m_terrain = terrain;
    this.m_cbBuffer = ctx * cty;
    this.m_abBuffer = new Uint8Array(this.m_cbBuffer);
    for (let n = 0; n < 8; n++) {
      this.m_mpDirDelta[n] = g_mpDirToDy[n] * this.m_ctx + g_mpDirToDx[n];
    }
    return true;
  }

  GetWidth(): number {
    return this.m_ctx;
  }
  GetHeight(): number {
    return this.m_cty;
  }
  GetMapPtr(): Uint8Array {
    return this.m_terrain;
  }

  // game/terrainmap.cpp:97-104 (MP_DEBUG) — the C++-vs-TS buffer-parity oracle. Compare
  // after masking transient scratch: sum of (byte & 0xE0) is the stable form.
  GetChecksum(): number {
    let n = 0;
    for (let i = 0; i < this.m_cbBuffer; i++) n = (n + this.m_abBuffer[i]) >>> 0;
    return n;
  }
  GetMaskedChecksum(): number {
    let n = 0;
    for (let i = 0; i < this.m_cbBuffer; i++) n = (n + (this.m_abBuffer[i] & kbClear)) >>> 0;
    return n;
  }

  // game/terrainmap.cpp:125-139
  SetFlags(tx: number, ty: number, ctx: number, cty: number, bf: number): void {
    for (let txT = tx; txT < tx + ctx; txT++) {
      for (let tyT = ty; tyT < ty + cty; tyT++) {
        this.m_abBuffer[tyT * this.m_ctx + txT] |= bf;
      }
    }
  }

  // game/terrainmap.cpp:141-156 — off-map counts as occupied
  TestFlags(tx: number, ty: number, ctx: number, cty: number, bf: number): boolean {
    if (tx < 0 || ty < 0 || tx + ctx > this.m_ctx || ty + cty > this.m_cty) return true;
    for (let txT = tx; txT < tx + ctx; txT++) {
      for (let tyT = ty; tyT < ty + cty; tyT++) {
        if (this.m_abBuffer[tyT * this.m_ctx + txT] & bf) return true;
      }
    }
    return false;
  }

  // game/terrainmap.cpp:158-175 — flags OR terrain > open
  IsOccupied(tx: number, ty: number, ctx: number, cty: number, bf: number): boolean {
    if (tx < 0 || ty < 0 || tx + ctx > this.m_ctx || ty + cty > this.m_cty) return true;
    for (let txT = tx; txT < tx + ctx; txT++) {
      for (let tyT = ty; tyT < ty + cty; tyT++) {
        const off = tyT * this.m_ctx + txT;
        if (this.m_abBuffer[off] & bf) return true;
        if (this.m_terrain[off] > kttOpen) return true;
      }
    }
    return false;
  }

  // game/terrainmap.cpp:177-191
  ClearFlags(tx: number, ty: number, ctx: number, cty: number, bf: number): void {
    for (let txT = tx; txT < tx + ctx; txT++) {
      for (let tyT = ty; tyT < ty + cty; tyT++) {
        this.m_abBuffer[tyT * this.m_ctx + txT] &= ~bf;
      }
    }
  }

  // game/terrainmap.cpp:193-252 — Bresenham walk; 'dx > dy' STRICT (ties run y-major)
  IsLineOccupied(
    txFrom: number,
    tyFrom: number,
    txTo: number,
    tyTo: number,
    bfTerrainAvoid: number,
  ): boolean {
    if (txFrom === txTo && tyFrom === tyTo) return false;

    const dx = Math.abs(txTo - txFrom);
    const dy = Math.abs(tyTo - tyFrom);
    const sx = txFrom < txTo ? 1 : -1;
    const sy = tyFrom < tyTo ? 1 : -1;

    let txT = txFrom;
    let tyT = tyFrom;
    let dc = 0;
    while (true) {
      if (dx > dy) {
        txT += sx;
        dc += dy;
        if (dc >= dx) {
          dc -= dx;
          tyT += sy;
        }
      } else {
        tyT += sy;
        dc += dx;
        if (dc >= dy) {
          dc -= dy;
          txT += sx;
        }
      }
      if (this.IsBlocked(txT, tyT, bfTerrainAvoid)) return true;
      if (txT === txTo && tyT === tyTo) break;
    }
    return false;
  }

  // game/terrainmap.cpp:254-321
  FindFirstUnoccupied(txFrom: number, tyFrom: number, txTo: number, tyTo: number): TPoint | null {
    const dx = Math.abs(txTo - txFrom);
    const dy = Math.abs(tyTo - tyFrom);
    const sx = txFrom < txTo ? 1 : -1;
    const sy = tyFrom < tyTo ? 1 : -1;

    let txT = txFrom;
    let tyT = tyFrom;
    if (!this.IsBlocked(txT, tyT, 0)) return { tx: txT, ty: tyT };
    if (txFrom === txTo && tyFrom === tyTo) return null;

    let dc = 0;
    while (true) {
      if (dx > dy) {
        txT += sx;
        dc += dy;
        if (dc >= dx) {
          dc -= dx;
          tyT += sy;
        }
      } else {
        tyT += sy;
        dc += dx;
        if (dc >= dy) {
          dc -= dy;
          txT += sx;
        }
      }
      if (!this.IsBlocked(txT, tyT, 0)) return { tx: txT, ty: tyT };
      if (txT === txTo && tyT === tyTo) break;
    }
    return null;
  }

  // game/terrainmap.cpp:323-385 — walks while unblocked; remembers the LAST free tile
  FindLastUnoccupied(txFrom: number, tyFrom: number, txTo: number, tyTo: number): TPoint | null {
    const dx = Math.abs(txTo - txFrom);
    const dy = Math.abs(tyTo - tyFrom);
    const sx = txFrom < txTo ? 1 : -1;
    const sy = tyFrom < tyTo ? 1 : -1;

    let txT = txFrom;
    let tyT = tyFrom;
    if (this.IsBlocked(txT, tyT, 0)) return null;
    let free: TPoint = { tx: txT, ty: tyT };
    if (txFrom === txTo && tyFrom === tyTo) return free;

    let dc = 0;
    while (true) {
      if (dx > dy) {
        txT += sx;
        dc += dy;
        if (dc >= dx) {
          dc -= dx;
          tyT += sy;
        }
      } else {
        tyT += sy;
        dc += dx;
        if (dc >= dy) {
          dc -= dy;
          txT += sx;
        }
      }
      if (this.IsBlocked(txT, tyT, 0)) return free;
      free = { tx: txT, ty: tyT };
      if (txT === txTo && tyT === tyTo) break;
    }
    return free;
  }

  // game/terrainmap.cpp:387-458
  FindLinePath(
    txFrom: number,
    tyFrom: number,
    txTo: number,
    tyTo: number,
    bfTerrainAvoid: number,
  ): Path | null {
    if (txFrom === txTo && tyFrom === tyTo) return null;

    const atpt: TPoint[] = [];
    const dx = Math.abs(txTo - txFrom);
    const dy = Math.abs(tyTo - tyFrom);
    const sx = txFrom < txTo ? 1 : -1;
    const sy = tyFrom < tyTo ? 1 : -1;

    let txT = txFrom;
    let tyT = tyFrom;
    let dc = 0;
    while (true) {
      if (dx > dy) {
        txT += sx;
        dc += dy;
        if (dc >= dx) {
          dc -= dx;
          tyT += sy;
        }
      } else {
        tyT += sy;
        dc += dx;
        if (dc >= dy) {
          dc -= dy;
          txT += sx;
        }
      }
      if (this.IsBlocked(txT, tyT, bfTerrainAvoid)) return null;
      atpt.push({ tx: txT & 0xff, ty: tyT & 0xff }); // (byte) stores
      if (txT === txTo && tyT === tyTo) break;
    }
    return CreatePathFromPoints(this, txFrom, tyFrom, atpt);
  }

  // game/terrainmap.cpp:460-647 — the best-first search
  FindPath(
    txFrom: number,
    tyFrom: number,
    txTo: number,
    tyTo: number,
    bfTerrainAvoid: number,
  ): Path | null {
    if (txFrom === txTo && tyFrom === tyTo) return null;

    // adjacency quick-out: a 1-step path with NO blockage check (deliberate)
    if (Math.abs(txFrom - txTo) <= 1 && Math.abs(tyFrom - tyTo) <= 1) {
      return CreatePathFromPoints(this, txFrom, tyFrom, [{ tx: txTo, ty: tyTo }]);
    }

    // clear transient scratch (the aligned fast-clear, value-identical per-byte form)
    for (let n = 0; n < this.m_cbBuffer; n++) this.m_abBuffer[n] &= kbClear;

    while (this.m_ppathhList !== null) this.RemovePathHead(this.m_ppathhList);

    const offTo = tyTo * this.m_ctx + txTo;

    // the start node lives OUTSIDE the open list (a stack local in the C++)
    const pathh: PathHead = {
      ppathhNext: null,
      txLast: txFrom,
      tyLast: tyFrom,
      cSteps: 0,
      offHead: tyFrom * this.m_ctx + txFrom,
      wScoreKnown: 0,
      wScore: 0,
    };
    let ppathhLast: PathHead | null = pathh;

    this.m_abBuffer[pathh.offHead] |= kbfLinked;

    let offClosest = 0;
    let nDistClosest = 0xffff; // (word)-1
    while (ppathhLast !== null) {
      for (let n = 0; n < 8; n++) {
        const txNew = ppathhLast.txLast + g_mpDirToDx[n];
        const tyNew = ppathhLast.tyLast + g_mpDirToDy[n];

        if (txNew < 0 || txNew >= this.m_ctx || tyNew < 0 || tyNew >= this.m_cty) continue;

        const offNew = ppathhLast.offHead + this.m_mpDirDelta[n];
        if (this.m_abBuffer[offNew] & kbfLinked) continue;

        // the tile it came from is no longer a head; if it's a list node, copy it into
        // the local and recycle the node (the C++ pathh = *ppathhLast dance)
        if (this.m_abBuffer[ppathhLast.offHead] & kbfHead) {
          this.m_abBuffer[ppathhLast.offHead] &= ~kbfHead;
          if (ppathhLast !== pathh) {
            pathh.txLast = ppathhLast.txLast;
            pathh.tyLast = ppathhLast.tyLast;
            pathh.cSteps = ppathhLast.cSteps;
            pathh.offHead = ppathhLast.offHead;
            pathh.wScoreKnown = ppathhLast.wScoreKnown;
            pathh.wScore = ppathhLast.wScore;
            this.RemovePathHead(ppathhLast);
            ppathhLast = pathh;
          }
        }

        const offHead = offNew & 0xffff;
        let wScoreTerrain: number;
        if (this.m_abBuffer[offNew] & bfTerrainAvoid) {
          wScoreTerrain = knScoreStructure;
        } else {
          wScoreTerrain = s_anScoreTerrain[this.m_terrain[offHead]];
        }

        // link in: keep persistent bits, set head+linked+DIRECTION (low 3 bits)
        this.m_abBuffer[offNew] = (this.m_abBuffer[offNew] & kbClear) | kbfHead | kbfLinked | n;
        const wScoreKnown = (ppathhLast.wScoreKnown + s_anScoreVector[n] + wScoreTerrain) & 0xffff;
        const nDist =
          (ganSquared[Math.abs(txNew - txTo)] + ganSquared[Math.abs(tyNew - tyTo)]) & 0xffff;
        const wScore = (wScoreKnown + nDist) & 0xffff;
        const ppathhNew = this.AddPathHead(
          wScoreKnown,
          wScore,
          ppathhLast.cSteps + 1,
          offHead,
          txNew,
          tyNew,
        );

        if (nDist < nDistClosest) {
          nDistClosest = nDist;
          offClosest = offNew;
        }

        // node cap reached: best path so far, toward the closest point seen
        if (ppathhNew === null) return this.MakePath(txFrom, tyFrom, offClosest);

        if (offNew === offTo) return this.MakePath(txFrom, tyFrom, ppathhNew.offHead);
      }

      // dead end? (only if the expanding node is still the list head)
      if (ppathhLast === this.m_ppathhList) this.RemovePathHead(ppathhLast);

      ppathhLast = this.m_ppathhList;
    }

    return null;
  }

  // game/terrainmap.cpp:649-691 — free-list alloc, lifetime node cap, '<='-LIFO insertion
  private AddPathHead(
    wScoreKnown: number,
    wScore: number,
    cSteps: number,
    offHead: number,
    txNew: number,
    tyNew: number,
  ): PathHead | null {
    let ppathh: PathHead;
    if (this.m_ppathhFreeList !== null) {
      ppathh = this.m_ppathhFreeList;
      this.m_ppathhFreeList = ppathh.ppathhNext;
    } else {
      if (this.m_cNodes >= kcNodesMax) return null;
      ppathh = {
        ppathhNext: null,
        txLast: 0,
        tyLast: 0,
        cSteps: 0,
        offHead: 0,
        wScoreKnown: 0,
        wScore: 0,
      };
      this.m_cNodes++;
    }

    ppathh.txLast = txNew;
    ppathh.tyLast = tyNew;
    ppathh.cSteps = cSteps;
    ppathh.offHead = offHead;
    ppathh.wScoreKnown = wScoreKnown;
    ppathh.wScore = wScore;

    // sorted insertion: new node goes BEFORE equal scores
    if (this.m_ppathhList === null || ppathh.wScore <= this.m_ppathhList.wScore) {
      ppathh.ppathhNext = this.m_ppathhList;
      this.m_ppathhList = ppathh;
    } else {
      let p = this.m_ppathhList;
      while (p.ppathhNext !== null && ppathh.wScore > p.ppathhNext.wScore) p = p.ppathhNext;
      ppathh.ppathhNext = p.ppathhNext;
      p.ppathhNext = ppathh;
    }
    return ppathh;
  }

  // game/terrainmap.cpp:694-705
  private RemovePathHead(ppathh: PathHead): void {
    if (this.m_ppathhList === ppathh) {
      this.m_ppathhList = ppathh.ppathhNext;
    } else {
      let p = this.m_ppathhList;
      while (p !== null && p.ppathhNext !== ppathh) p = p.ppathhNext;
      if (p === null) return;
      p.ppathhNext = ppathh.ppathhNext;
    }
    ppathh.ppathhNext = this.m_ppathhFreeList;
    this.m_ppathhFreeList = ppathh;
  }

  // game/terrainmap.cpp:743-764 — walk the stored direction bits back to the start
  private MakePath(txStart: number, tyStart: number, off: number): Path | null {
    const adir: number[] = [];
    let ty = Math.floor(off / this.m_ctx);
    let tx = off % this.m_ctx;
    let offWalk = off;
    while (tx !== txStart || ty !== tyStart) {
      const dir = this.m_abBuffer[offWalk] & kbfDirMask;
      adir.unshift(dir);
      const dirOpposite = gmpdirdirOpposite[dir];
      offWalk += this.m_mpDirDelta[dirOpposite];
      tx += g_mpDirToDx[dirOpposite];
      ty += g_mpDirToDy[dirOpposite];
    }
    return CreatePathFromDirs(this, txStart, tyStart, adir);
  }

  // game/terrainmap.cpp:707-722 — blocked = flags hit OR terrain > open
  IsBlocked(tx: number, ty: number, bf: number): boolean {
    const offMap = ty * this.m_ctx + tx;
    if (this.m_abBuffer[offMap] & bf) return true;
    if (this.m_terrain[offMap] > kttOpen) return true;
    return false;
  }

  // game/terrainmap.cpp:724-730
  GetTerrainType(tx: number, ty: number): number {
    return this.m_terrain[ty * this.m_ctx + tx];
  }

  // game/terrainmap.cpp:731-742 — false (no flags) when the terrain itself blocks
  GetFlags(tx: number, ty: number): number | null {
    const offMap = ty * this.m_ctx + tx;
    if (this.m_terrain[offMap] > kttOpen) return null;
    return this.m_abBuffer[offMap];
  }

  // game/terrainmap.cpp:106-118 (knVerTerrainMapState 1). NOTE the C++ SaveState
  // MUTATES the live buffer first, stripping every bit except mobile-unit/structure/
  // reserved — bug-compat (sim-quirks B5): the strip persists in the running game after a save.
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    for (let n = 0; n < this.m_cbBuffer; n++) {
      this.m_abBuffer[n] &= kbfMobileUnit | kbfStructure | kbfReserved;
    }
    pstm.WriteBytesRLE(this.m_abBuffer.subarray(0, this.m_cbBuffer));
    return pstm.IsSuccess();
  }

  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    pstm.ReadBytesRLE(this.m_abBuffer.subarray(0, this.m_cbBuffer));
    return pstm.IsSuccess();
  }
}

export class Path {
  m_ptrmap!: TerrainMap;
  m_cdirs = 0;
  m_adir: number[] = [];
  m_txStart = 0;
  m_tyStart = 0;
  // the follow cache: PERSISTED sim state (saved/loaded with the path)
  m_idirCache = -1;
  m_txCache = 0;
  m_tyCache = 0;

  // game/terrainmap.cpp:807-826
  Init(ptrmap: TerrainMap, txStart: number, tyStart: number, adir: number[]): boolean {
    this.m_adir = adir.slice();
    this.m_cdirs = adir.length;
    this.m_txStart = s16(txStart);
    this.m_tyStart = s16(tyStart);
    this.m_idirCache = -1;
    this.m_txCache = this.m_txStart;
    this.m_tyCache = this.m_tyStart;
    this.m_ptrmap = ptrmap;
    return true;
  }

  GetCount(): number {
    return this.m_cdirs;
  } // :828-831

  // game/terrainmap.cpp:833-847
  GetPoint(itpt: number, bf: number): TPoint | null {
    if (itpt < 0 || itpt >= this.m_cdirs) return null;
    const pt = this.CalcTo(itpt);
    if (this.m_ptrmap.IsBlocked(pt.tx, pt.ty, bf)) return null;
    return pt;
  }

  // game/terrainmap.cpp:849-858
  GetPointRaw(itpt: number): TPoint | null {
    if (itpt < 0 || itpt >= this.m_cdirs) return null;
    return this.CalcTo(itpt);
  }

  // game/terrainmap.cpp:860-869
  SetCacheIndex(idir: number): void {
    if (idir < 0 || idir >= this.m_cdirs) return;
    const pt = this.CalcTo(idir);
    this.m_idirCache = idir;
    this.m_txCache = pt.tx;
    this.m_tyCache = pt.ty;
  }

  // game/terrainmap.cpp:872-894 — backward seek resets and recomputes (the RELEASE path)
  private CalcTo(idir: number): TPoint {
    if (idir < this.m_idirCache) {
      this.m_idirCache = -1;
      this.m_txCache = this.m_txStart;
      this.m_tyCache = this.m_tyStart;
    }
    let idirT = this.m_idirCache;
    let txT = this.m_txCache;
    let tyT = this.m_tyCache;
    while (true) {
      idirT++;
      if (idirT > idir) break;
      txT = s16(txT + g_mpDirToDx[this.m_adir[idirT]]);
      tyT = s16(tyT + g_mpDirToDy[this.m_adir[idirT]]);
    }
    return { tx: txT, ty: tyT };
  }

  // game/terrainmap.cpp:896-942 — u16 distances; '<=' keeps the LATEST equal-distance point
  FindClosestPoint(
    txFrom: number,
    tyFrom: number,
    itptStart: number,
    ctptFurtherStop: number,
  ): { itpt: number; tpt: TPoint } | null {
    const ctptRemaining = this.m_cdirs - itptStart;
    let n2Last = 0xffff;
    let nSmallest = 0xffff;
    let jSmallest = -1;
    let tptClosest: TPoint = { tx: 0, ty: 0 };
    let ctptFurtherStopT = ctptFurtherStop;
    for (let j = 0; j < ctptRemaining; j++) {
      const tpt = this.GetPointRaw(j + itptStart);
      if (tpt === null) break;
      if (tpt.tx === txFrom && tpt.ty === tyFrom) {
        return { itpt: j + itptStart, tpt };
      }
      const n2 =
        (ganSquared[Math.abs(tpt.tx - txFrom)] + ganSquared[Math.abs(tpt.ty - tyFrom)]) & 0xffff;
      if (n2 <= nSmallest) {
        nSmallest = n2;
        jSmallest = j;
        tptClosest = tpt;
      }
      if (n2 > n2Last) {
        ctptFurtherStopT--;
        if (ctptFurtherStopT === 0) break;
      } else {
        ctptFurtherStopT = ctptFurtherStop;
      }
      n2Last = n2;
    }
    if (jSmallest === -1) return null;
    return { itpt: jSmallest + itptStart, tpt: tptClosest };
  }

  // game/terrainmap.cpp:944-956
  Append(ppath: Path): boolean {
    this.m_adir = this.m_adir.concat(ppath.m_adir);
    this.m_cdirs += ppath.m_cdirs;
    return true;
  }

  // game/terrainmap.cpp:957-972
  TrimEnd(itptStart: number): boolean {
    if (itptStart >= this.m_cdirs) return false;
    this.m_adir = this.m_adir.slice(0, itptStart);
    this.m_cdirs = itptStart;
    return true;
  }

  // game/terrainmap.cpp:974-995 — the clone RESETS the follow cache
  Clone(): Path {
    const p = new Path();
    p.m_ptrmap = this.m_ptrmap;
    p.m_cdirs = this.m_cdirs;
    p.m_txStart = this.m_txStart;
    p.m_tyStart = this.m_tyStart;
    p.m_idirCache = -1;
    p.m_txCache = this.m_txStart;
    p.m_tyCache = this.m_tyStart;
    p.m_adir = this.m_adir.slice();
    return p;
  }

  GetStartPoint(): TPoint {
    return { tx: this.m_txStart, ty: this.m_tyStart };
  } // :995-1000

  // game/terrainmap.cpp:1001-1060 (knVerPathState 4) — 2 directions per byte, high
  // nibble first. The version is 4 in the shipping client (verified by savegame-xray).
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(4);
    pstm.WriteWord(this.m_cdirs);
    pstm.WriteWord(this.m_txStart);
    pstm.WriteWord(this.m_tyStart);
    pstm.WriteWord(this.m_idirCache);
    pstm.WriteWord(this.m_txCache);
    pstm.WriteWord(this.m_tyCache);
    let bT = 0;
    for (let idir = 0; idir < this.m_cdirs; idir++) {
      const dir = this.m_adir[idir];
      if ((idir & 1) === 0) {
        bT = (dir << 4) & 0xff;
        if (idir === this.m_cdirs - 1) pstm.WriteByte(bT);
      } else {
        bT |= dir & 0x0f;
        pstm.WriteByte(bT);
      }
    }
    return pstm.IsSuccess();
  }

  LoadState(ptrmap: TerrainMap, pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 4) return false; // knVerPathState 4
    this.m_ptrmap = ptrmap;
    this.m_cdirs = pstm.ReadWord();
    this.m_txStart = (pstm.ReadWord() << 16) >> 16;
    this.m_tyStart = (pstm.ReadWord() << 16) >> 16;
    this.m_adir = new Array(this.m_cdirs);
    this.m_idirCache = (pstm.ReadWord() << 16) >> 16;
    this.m_txCache = (pstm.ReadWord() << 16) >> 16;
    this.m_tyCache = (pstm.ReadWord() << 16) >> 16;
    let bT = 0;
    for (let idir = 0; idir < this.m_cdirs; idir++) {
      let dir: number;
      if ((idir & 1) === 0) {
        bT = pstm.ReadByte();
        dir = (bT & 0xf0) >> 4;
      } else {
        dir = bT & 0x0f;
      }
      this.m_adir[idir] = dir;
    }
    return pstm.IsSuccess();
  }
}

// game/terrainmap.cpp:769-782
export function CreatePathFromPoints(
  ptrmap: TerrainMap,
  txStart: number,
  tyStart: number,
  atpt: TPoint[],
): Path | null {
  const adir: number[] = [];
  let txLast = txStart;
  let tyLast = tyStart;
  for (const tpt of atpt) {
    adir.push(DirectionFromLocations(txLast, tyLast, tpt.tx, tpt.ty));
    txLast = tpt.tx;
    tyLast = tpt.ty;
  }
  return CreatePathFromDirs(ptrmap, txStart, tyStart, adir);
}

// game/terrainmap.cpp:784-795
export function CreatePathFromDirs(
  ptrmap: TerrainMap,
  txStart: number,
  tyStart: number,
  adir: number[],
): Path | null {
  const ppath = new Path();
  if (!ppath.Init(ptrmap, txStart, tyStart, adir)) return null;
  return ppath;
}

// game/terrainmap.cpp:1087-1120
export function DirectionFromLocations(
  txOld: number,
  tyOld: number,
  txNew: number,
  tyNew: number,
): number {
  if (txNew < txOld) {
    if (tyNew < tyOld) return kdirNW;
    if (tyNew > tyOld) return kdirSW;
    return kdirW;
  }
  if (txNew > txOld) {
    if (tyNew < tyOld) return kdirNE;
    if (tyNew > tyOld) return kdirSE;
    return kdirE;
  }
  if (tyNew < tyOld) return kdirN;
  if (tyNew > tyOld) return kdirS;
  return kdirInvalid;
}

// game/terrainmap.cpp:1122-1129 — preserves the kdirInvalid(-1) sentinel: -1*2 would no
// longer match kdir16Invalid and no facing (0-15) could ever equal it.
export function Direction16FromLocations(
  txOld: number,
  tyOld: number,
  txNew: number,
  tyNew: number,
): number {
  const dir = DirectionFromLocations(txOld, tyOld, txNew, tyNew);
  return dir === kdirInvalid ? kdir16Invalid : dir * 2;
}

// game/ht.h:1478-1507 + terrainmap.cpp:1134-1218 — progress measurement against a path.
// "Progress" along the path is judged by closest-point index first, then squared distances
// to the points before/after the closest point.
export class TrackPoint {
  m_tptInitial: TPoint = { tx: 0, ty: 0 };
  m_tptClosest: TPoint = { tx: 0, ty: 0 };
  m_tptBefore: TPoint = { tx: 0, ty: 0 };
  m_tptAfter: TPoint = { tx: 0, ty: 0 };
  m_itptClosest = 0;
  m_n2Before = 0;
  m_n2After = 0;

  // terrainmap.cpp:1134-1157
  Init(
    ppath: Path,
    txFrom: number,
    tyFrom: number,
    itptStart: number,
    ctptFurtherStop: number,
  ): boolean {
    const closest = ppath.FindClosestPoint(txFrom, tyFrom, itptStart, ctptFurtherStop);
    if (closest === null) return false;
    this.m_itptClosest = closest.itpt;
    this.m_tptClosest = closest.tpt;

    this.m_tptBefore = ppath.GetPoint(this.m_itptClosest - 1, 0) ?? this.m_tptClosest;
    this.m_tptAfter = ppath.GetPoint(this.m_itptClosest + 1, 0) ?? this.m_tptClosest;

    this.m_n2Before =
      ganSquared[Math.abs(txFrom - this.m_tptBefore.tx)] +
      ganSquared[Math.abs(tyFrom - this.m_tptBefore.ty)];
    this.m_n2After =
      ganSquared[Math.abs(txFrom - this.m_tptAfter.tx)] +
      ganSquared[Math.abs(tyFrom - this.m_tptAfter.ty)];
    this.m_tptInitial = { tx: txFrom, ty: tyFrom };
    return true;
  }

  // terrainmap.cpp:1159-1168
  InitFrom(ptrkp: TrackPoint): void {
    this.m_tptInitial = { ...ptrkp.m_tptInitial };
    this.m_tptClosest = { ...ptrkp.m_tptClosest };
    this.m_tptBefore = { ...ptrkp.m_tptBefore };
    this.m_tptAfter = { ...ptrkp.m_tptAfter };
    this.m_itptClosest = ptrkp.m_itptClosest;
    this.m_n2Before = ptrkp.m_n2Before;
    this.m_n2After = ptrkp.m_n2After;
  }

  // terrainmap.cpp:1170-1190 — is ptrkpA further along the path than this?
  IsProgress(ptrkpA: TrackPoint): boolean {
    // ptrkpB in the C++ is `this`.
    if (ptrkpA.m_itptClosest < this.m_itptClosest) return false;
    if (ptrkpA.m_itptClosest > this.m_itptClosest) return true;

    return (
      (ptrkpA.m_n2Before === this.m_n2Before && ptrkpA.m_n2After < this.m_n2After) ||
      (ptrkpA.m_n2Before > this.m_n2Before && ptrkpA.m_n2After <= this.m_n2After) ||
      (ptrkpA.m_n2Before < this.m_n2Before && ptrkpA.m_n2After < this.m_n2After)
    );
  }

  // terrainmap.cpp:1192-1203
  IsCloser(ptrkpA: TrackPoint): boolean {
    // ptrkpB in the C++ is `this`.
    if (ptrkpA.m_n2After < this.m_n2After) return true;
    if (ptrkpA.m_n2After === this.m_n2After) return this.IsProgress(ptrkpA);
    return false;
  }

  // terrainmap.cpp:1205-1218
  IsBetterSort(ptrkpA: TrackPoint): boolean {
    return this.IsProgress(ptrkpA);
  }

  GetClosestPoint(): TPoint {
    return { ...this.m_tptClosest };
  } // ht.h:1486
  GetClosestPointIndex(): number {
    return this.m_itptClosest;
  } // ht.h:1489
  GetAfterPoint(): TPoint {
    return { ...this.m_tptAfter };
  } // ht.h:1492
  GetInitialPoint(): TPoint {
    return { ...this.m_tptInitial };
  } // ht.h:1495
}
